import { useEffect, useRef, useState } from "react";
import { Sparkles, Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, img } from "../../lib/api";
import { slugify, stripHtml } from "../../lib/i18n";
import { useAdmin } from "../../pages/admin/AdminApp";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";

export function Field({ label, hint, children, className = "" }) {
  return (
    <div className={className}>
      {label && <Label className="text-[13px] font-semibold text-stone-700">{label}</Label>}
      <div className="mt-1.5">{children}</div>
      {hint && <p className="text-xs text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

export function useTranslate() {
  const { aiAvailable } = useAdmin();
  const [busy, setBusy] = useState(false);
  const run = async (text, target) => {
    setBusy(true);
    try {
      const r = await api.post("/admin/ai/translate", { fields: { v: text }, source: "tr", target });
      return r.data.translations.v;
    } catch (e) { toast.error(errMsg(e)); return null; }
    finally { setBusy(false); }
  };
  return { aiAvailable, busy, run };
}

export function LangTabs({ lang, setLang, value, onTranslate, busy, testid }) {
  const { langs, aiAvailable } = useAdmin();
  return (
    <div className="flex items-center gap-1 mb-1.5">
      {langs.map((l) => (
        <button type="button" key={l} onClick={() => setLang(l)} data-testid={`${testid}-lang-${l}`}
          className={`text-[11px] uppercase font-bold px-2 py-0.5 rounded ${lang === l ? "bg-stone-900 text-white" : "text-stone-400 hover:text-stone-700"}`}>
          {l}{l !== "tr" && !value?.[l] ? " ·" : ""}
        </button>
      ))}
      {lang !== "tr" && (
        <button type="button" disabled={!aiAvailable || !value?.tr || busy} onClick={onTranslate} data-testid={`${testid}-ai-translate`}
          title={aiAvailable ? "Türkçeden çevir" : "Ayarlar > Yapay Zeka'dan anahtar ekleyin"}
          className="ml-auto text-[11px] flex items-center gap-1 text-violet-700 disabled:text-stone-300">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Yapay zeka ile çevir
        </button>
      )}
    </div>
  );
}

export function I18nInput({ label, value = {}, onChange, multiline, rows = 3, testid, maxLength, hint }) {
  const [lang, setLang] = useState("tr");
  const { busy, run } = useTranslate();
  const translate = async () => { const r = await run(value.tr, lang); if (r) onChange({ ...value, [lang]: r }); };
  const Comp = multiline ? Textarea : Input;
  return (
    <Field label={label} hint={hint}>
      <LangTabs lang={lang} setLang={setLang} value={value} onTranslate={translate} busy={busy} testid={testid} />
      <Comp rows={rows} maxLength={maxLength} value={value?.[lang] || ""} onChange={(e) => onChange({ ...value, [lang]: e.target.value })}
        data-testid={testid} placeholder={lang !== "tr" && value?.tr ? `(boşsa Türkçesi gösterilir)` : ""} />
      {maxLength && <p className="text-[11px] text-stone-400 text-right mt-0.5">{(value?.[lang] || "").length}/{maxLength}</p>}
    </Field>
  );
}

export function AudioUpload({ label, value, onChange, testid }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const okTypes = ["audio/mpeg", "audio/mp3", "audio/wav", "audio/x-wav", "audio/wave", "audio/ogg", "audio/mp4", "audio/x-m4a", "audio/aac"];
    if (!okTypes.includes(file.type) && !/\.(mp3|wav|ogg|m4a|aac)$/i.test(file.name || "")) {
      return toast.error(`Bu dosya türü (${file.type || "bilinmiyor"}) desteklenmiyor. MP3, WAV, OGG, M4A, AAC seçin.`);
    }
    if (file.size > 50 * 1024 * 1024) return toast.error("Ses dosyası 50 MB'den büyük olamaz");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try { onChange((await api.post("/admin/audio-upload", fd)).data.url); toast.success("Ses yüklendi"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <Field label={label} hint="MP3, WAV, OGG, M4A, AAC — en fazla 50 MB.">
      <div className="flex items-center gap-3">
        <input ref={ref} type="file" accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-input`} />
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current.click()} data-testid={`${testid}-button`}>
          {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />} Ses yükle
        </Button>
        {value && (
          <>
            <audio src={img(value)} controls className="h-8 flex-1 min-w-0" />
            <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testid}-clear`}><X size={14} /></Button>
          </>
        )}
      </div>
    </Field>
  );
}

export function ImageUpload({ label, value, onChange, testid, alt }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return toast.error(`Bu dosya türü (${file.type || "bilinmiyor"}) desteklenmiyor. Lütfen JPEG veya PNG seçin.`);
    if (file.size > 10 * 1024 * 1024) return toast.error("Dosya 10 MB'den büyük olamaz");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("alt", alt || "");
    setBusy(true);
    try { onChange((await api.post("/admin/upload", fd)).data.url); toast.success("Görsel yüklendi (WebP)"); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <Field label={label} hint="JPEG/PNG, en fazla 10 MB. Otomatik WebP'ye çevrilir, gizli bilgiler (EXIF) silinir.">
      <div className="flex items-center gap-3">
        <div className="w-24 h-24 rounded-lg border bg-stone-100 overflow-hidden flex items-center justify-center shrink-0">
          {value ? <img src={img(value)} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" /> : <Upload size={18} className="text-stone-300" />}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={ref} type="file" accept="image/jpeg,image/png" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={`${testid}-input`} />
          <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => ref.current.click()} data-testid={`${testid}-button`}>
            {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Upload size={14} className="mr-1" />} Yükle
          </Button>
          {value && <Button type="button" variant="ghost" size="sm" onClick={() => onChange("")} data-testid={`${testid}-clear`}><X size={14} className="mr-1" /> Kaldır</Button>}
        </div>
      </div>
    </Field>
  );
}

export function SlugField({ value, onChange, source, prefix, testid = "slug-input" }) {
  const auto = slugify(source || "");
  return (
    <Field label="Adres (URL)" hint="Boş bırakılırsa başlıktan otomatik oluşur. Değiştirirseniz eski adres otomatik yönlendirilir.">
      <div className="flex items-center rounded-md border bg-stone-50 overflow-hidden">
        <span className="px-3 text-xs text-stone-400 whitespace-nowrap">{prefix}</span>
        <input className="flex-1 bg-white px-2 py-2 text-sm outline-none" value={value || ""} placeholder={auto} onChange={(e) => onChange(slugify(e.target.value))} data-testid={testid} />
      </div>
    </Field>
  );
}

export const autoDesc = (html, n = 155) => {
  const t = stripHtml(html || "").replace(/\s+/g, " ").trim();
  if (t.length <= n) return t;
  const cut = t.slice(0, n);
  const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("? "), cut.lastIndexOf("! "));
  return end > 90 ? cut.slice(0, end + 1) : `${cut.slice(0, cut.lastIndexOf(" ")).replace(/[,;:-]+$/, "")}…`;
};

export function SeoFields({ value = {}, onChange, title, body, path }) {
  const { langs } = useAdmin();
  const [lang, setLang] = useState("tr");
  const last = useRef({});
  const bodyKey = JSON.stringify(body || {});
  useEffect(() => {
    const desc = { ...(value.description || {}) };
    let changed = false;
    for (const l of langs) {
      const gen = autoDesc(body?.[l]);
      if (gen && (!desc[l] || desc[l] === last.current[l]) && desc[l] !== gen) { desc[l] = gen; changed = true; }
      last.current[l] = gen;
    }
    if (changed) onChange({ ...value, description: desc });
  }, [bodyKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const t = value.title?.[lang] || title?.[lang] || title?.tr || "";
  const d = value.description?.[lang] || autoDesc(body?.[lang] || body?.tr);
  const set = (k, v) => onChange({ ...value, [k]: { ...(value[k] || {}), [lang]: v } });
  const regen = () => { const g = autoDesc(body?.[lang] || body?.tr); if (!g) return toast.error("Önce içerik/açıklama yazın"); last.current[lang] = g; set("description", g); };
  return (
    <div className="border rounded-xl p-5 bg-white space-y-4" data-testid="seo-fields">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-sm">SEO & paylaşım</p>
        <div className="flex gap-1">{langs.map((l) => <button type="button" key={l} onClick={() => setLang(l)} className={`text-[11px] uppercase font-bold px-2 py-0.5 rounded ${lang === l ? "bg-stone-900 text-white" : "text-stone-400"}`}>{l}</button>)}</div>
      </div>
      <div className="rounded-lg border p-4 bg-stone-50" data-testid="google-preview">
        <p className="text-xs text-stone-500">{window.location.host}{path}</p>
        <p className="text-[#1a0dab] text-lg leading-tight truncate">{t || "Başlık"}</p>
        <p className="text-sm text-stone-600 line-clamp-2">{d || "Açıklama içerikten otomatik üretilir."}</p>
      </div>
      <Field label="SEO başlığı" hint={`${(value.title?.[lang] || "").length}/60 · boşsa içerik başlığı kullanılır`}>
        <Input maxLength={70} value={value.title?.[lang] || ""} placeholder={title?.[lang] || title?.tr} onChange={(e) => set("title", e.target.value)} data-testid="seo-title-input" />
      </Field>
      <Field label="SEO açıklaması" hint={`${(value.description?.[lang] || "").length}/160 · içerikten otomatik oluşturulur, isterseniz düzenleyin`}>
        <div className="flex justify-end -mt-1 mb-1"><button type="button" onClick={regen} className="text-xs text-stone-600 hover:text-stone-900 flex items-center gap-1" data-testid="seo-description-auto"><Sparkles size={12} />Otomatik oluştur</button></div>
        <Textarea rows={2} maxLength={170} value={value.description?.[lang] || ""} onChange={(e) => set("description", e.target.value)} data-testid="seo-description-input" />
      </Field>
    </div>
  );
}
